import * as React from 'react';
import { Form } from '@ant-design/compatible';
import { isNil } from 'lodash';
import { ColProps } from 'antd/lib/grid/col';
import { ValidationRule } from '@ant-design/compatible/lib/form/Form';
import { ValueOf } from '../../../../types/common/utils';
import { useFormInitialValue } from '../contexts/FormInitialValueContext';
import { useFormContext } from '../contexts/FormContext';
import CountLabel from './CountLabel';
import { countString } from '../../../../lib/utils/countCharacters';
import { FormItemStyle } from './style';

export const LAYOUT_TYPE = {
  HORIZONTAL: 'horizontal',
  VERTICAL: 'vertical',
} as const;
export type LAYOUT_TYPE = ValueOf<typeof LAYOUT_TYPE>;

const horizontalLayout = {
  labelCol: {
    lg: { span: 24 },
    xl: { span: 4 },
  },
  wrapperCol: {
    lg: { span: 24 },
    xl: { span: 20 },
  },
};

const verticalLayout = {
  labelCol: {
    span: 24,
  },
  wrapperCol: {
    span: 24,
  },
};

interface ItemProps<ValueType> {
  name?: string;
  notice?: React.ReactNode;
  className?: string;
  required?: boolean;
  label?: React.ReactNode;
  rules?: (ValidationRule & { disabled?: boolean })[];
  valuePropName?: string;
  layout?: {
    labelCol: ColProps;
    wrapperCol: ColProps;
  };
  layoutType?: LAYOUT_TYPE;
  initialValue?: unknown;
  maxCount?: number;
  normalize?: (value: ValueType, prevValue: ValueType, allValues: ValueType) => ValueType;
}

const Item = <ValueType,>(props: React.PropsWithChildren<ItemProps<ValueType>>): JSX.Element => {
  const {
    layout,
    layoutType = LAYOUT_TYPE.HORIZONTAL,
    notice,
    name,
    valuePropName = 'value',
    rules = [],
    className,
    required,
    label,
    maxCount,
    initialValue,
    normalize,
    children,
  } = props;
  const form = useFormContext();

  const isHorizontal = layoutType === LAYOUT_TYPE.HORIZONTAL;

  const formItemLayout = layout ? layout : isHorizontal ? horizontalLayout : verticalLayout;

  const formInitialValues = useFormInitialValue() as Record<string, unknown>;
  const formInitialValue = name && formInitialValues?.[name];

  if (form === null) throw new Error('form이 존재하지 않습니다. FormContext의 value를 확인해주세요.');

  const value = (name && form.getFieldValue(name)) || formInitialValue || '';

  return (
    <FormItemStyle>
      <Form.Item
        labelAlign="left"
        className={className}
        colon={false}
        required={required}
        label={
          maxCount ? (
            <CountLabel
              label={label}
              max={maxCount}
              length={typeof value === 'string' ? countString(value) : value.length}
              layoutType={layoutType === LAYOUT_TYPE.VERTICAL ? LAYOUT_TYPE.HORIZONTAL : LAYOUT_TYPE.VERTICAL}
            />
          ) : (
            label
          )
        }
        {...formItemLayout}
      >
        {name
          ? form.getFieldDecorator(name, {
              rules: rules.filter((rule) => !rule.disabled),
              valuePropName,
              initialValue: !isNil(formInitialValue) ? formInitialValue : initialValue,
              normalize,
            })(children)
          : children}
        {notice}
      </Form.Item>
    </FormItemStyle>
  );
};

export default Item;
